import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { chunk, isEmpty } from 'lodash';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import { fetchLandingPagePageData } from '../../store/shop/actions';
import {
  hasLoadedLandingPage,
  isLoadingLandingPage,
  getFeaturedProducts,
  getOverdueProducts,
  getShopCategories,
  getLandingPageLoadingError,
} from '../../store/shop/selectors';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import SeoHead from '../../components/seo';
import ShopLandingPage from '../../components/shop/landing-page';
import AssignmentLoader from '../../components/shop/assignments/loader';

class ShopLandingPageController extends React.Component {
  static async getInitialProps({ isServer, club, reduxStore, res, req }) {
    const initialProps = {
      activeSection: 'shop',
      namespacesRequired: ['common', 'shop'],
      pageType: 'shop.index',
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(fetchLandingPagePageData(club.id, req));
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  loadIfRequired = () => {
    const {
      hasPreviouslyLoaded,
      isLoading,
      loadingError,
      club: { id: clubId },
      loadPageData,
    } = this.props;
    if (!hasPreviouslyLoaded && !isLoading && !loadingError) {
      loadPageData(clubId);
    }
  };

  categoriesString = () => {
    const { categories: categoriesList = [''] } = this.props;
    if (isEmpty(categoriesList)) {
      return '';
    }

    if (categoriesList.length === 1) {
      return categoriesList[0];
    }

    const [categories, lastCategory] = chunk(categoriesList, categoriesList.length - 1);
    return `${categories.join(', ')} and ${lastCategory}`;
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const { isLoading, categories, featuredProducts, overdueProducts } = this.props;

    return (
      <>
        <SeoHead
          titleTranslationKey="shop:lading_page_title"
          descriptionTranslationKey="shop:landing_page_meta_description"
          route="shop"
          translationValues={{ categories: this.categoriesString() }}
        />
        <AssignmentLoader clubId={club.id} />
        <ShopLandingPage
          clubId={club.id}
          isLoading={isLoading}
          categories={categories}
          overdueProducts={overdueProducts}
          featuredProducts={featuredProducts}
        />
      </>
    );
  }
}

ShopLandingPageController.defaultProps = {
  loadingError: null,
};

ShopLandingPageController.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  featuredProducts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  overdueProducts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  hasPreviouslyLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadingError: PropTypes.shape(),
  loadPageData: PropTypes.func.isRequired,
};

const dispatchToProps = {
  loadPageData: fetchLandingPagePageData,
};

const mapStateToProps = (state) => ({
  hasPreviouslyLoaded: hasLoadedLandingPage(state),
  isLoading: isLoadingLandingPage(state),
  categories: getShopCategories(state),
  featuredProducts: getFeaturedProducts(state),
  overdueProducts: getOverdueProducts(state),
  loadingError: getLandingPageLoadingError(state),
});

export default connect(mapStateToProps, dispatchToProps)(ShopLandingPageController);
